/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";

export default class extends Controller {
  static targets = [
    "home",
    "mortgageBalance",
    "estCostSell",
    "estProfitSelling",
    "estCostSellPercentage",
    "realtorComSell",
    "realtorComSellPercentage",
    "purchasePrice",
    "downPayment",
    "downPaymentPercentage",
    "estimatedClosingCost",
    "estimatedClosingCostPercentage",
    "realtorComBuy",
    "realtorComBuyPercentage",
    "estimatedCostsBuy",
    "loanAmount",
    "interestRate",
    "loanTerm",
    "estimatedMortgagePayment",
    "cumulativeMonthlyCostDifference",
    "monthlyPaymentDifference",
    "equityOnNewHomePayments",
    "annualAppreciation",
    "firstChartWithLabel",
    "secondChartCanvas",
    "superTotal",
    "newHomePrice",
    "estimatedProfitChart",
    "estimatedCostToBuyChart",
    "monthlyCostChart",
    "downPaymentChart",
    "equityInNewHome",
    "estimatedProfitColor",
    "estimatedCostToBuyColor",
    "downPaymentColor",
    "monthlyCostColor",
    "appreciatedValueColor",
    "downPaymentColor2",
    "equityColor",
    "appreciatedValueChart",
    "downPayment2Chart",
    "equityInNewHomeChart",
    "pocketedSpent",
  ];

  static values = {
    home: Number,
    mortgageBalance: Number,
    estCostSell: Number,
    estCostSellPercentage: Number,
    realtorComSell: Number,
    realtorComSellPercentage: Number,
    estProfitFromSelling: Number,
    purchasePrice: Number,
    downPayment: Number,
    downPaymentPercentage: Number,

    estimatedClosingCost: Number,
    estimatedClosingCostPercentage: Number,
    realtorComBuy: Number,
    realtorComBuyPercentage: Number,
    estimatedCostsBuy: Number,

    loanAmount: Number,
    interestRate: Number,
    loanTerm: Number,
    estimatedMortgagePayment: Number,

    currentMortgagePayment: Number,
    cumulativeMonthlyCostDifference: Number,
    monthlyPaymentDifference: Number,
    equityOnNewHomePayments: Number,
    annualAppreciation: Number,

    totalCashAndEquity: Number,
    equityAfterThreeYears: Number,
    totalCashDifference: Number,
  };

  connect() {
    this.firstChart = null;
    this.displayValuesInInputs();
    this.updateFirstChartWithLabel();
    this.updateSecondChartData();
  }

  displayValuesInInputs() {
    this.homeTarget.value = this.formatCurrency(this.homeValue);
    this.mortgageBalanceTarget.value = this.formatCurrency(
      this.mortgageBalanceValue,
    );
    this.estCostSellTarget.value = this.formatCurrency(this.estCostSellValue);
    this.estCostSellPercentageTarget.value =
      this.estCostSellPercentageValue.toFixed(1);
    this.realtorComSellTarget.value = this.formatCurrency(
      this.realtorComSellValue,
    );
    this.realtorComSellPercentageTarget.value =
      this.realtorComSellPercentageValue.toFixed(1);
    this.purchasePriceTarget.value = this.formatCurrency(
      this.purchasePriceValue,
    );
    this.downPaymentTarget.value = this.formatCurrency(this.downPaymentValue);
    this.downPaymentPercentageTarget.value =
      this.downPaymentPercentageValue.toFixed(1);
    this.estimatedClosingCostTarget.value = this.formatCurrency(
      this.estimatedClosingCostValue,
    );
    this.estimatedClosingCostPercentageTarget.value =
      this.estimatedClosingCostPercentageValue.toFixed(1);
    this.realtorComBuyTarget.value = this.formatCurrency(
      this.realtorComBuyValue,
    );
    this.realtorComBuyPercentageTarget.value =
      this.realtorComBuyPercentageValue.toFixed(1);
    this.estimatedCostsBuyTarget.value = this.formatCurrency(
      this.estimatedCostsBuyValue,
    );
    this.loanAmountTarget.value = this.formatCurrency(this.loanAmountValue);
    this.interestRateTarget.value = this.interestRateValue.toFixed(1);
    this.estimatedMortgagePaymentTarget.value = this.formatCurrency(
      this.estimatedMortgagePaymentValue,
    );
    this.monthlyPaymentDifferenceTarget.value = this.formatCurrency(
      this.monthlyPaymentDifferenceValue,
    );
    this.equityOnNewHomePaymentsTarget.value = this.formatCurrency(
      this.equityOnNewHomePaymentsValue,
    );
    this.annualAppreciationTarget.value =
      this.annualAppreciationValue.toFixed(1);
    this.cumulativeMonthlyCostDifferenceTarget.value = this.formatCurrency(
      this.cumulativeMonthlyCostDifferenceValue,
    );
  }

  displayProfitSelling() {
    this.estProfitSellingTarget.value = this.formatCurrency(
      this.estProfitFromSellingValue,
    );
  }

  updateValuesFromInput() {
    this.homeValue = this.parseCurrency(this.homeTarget.value);
    this.mortgageBalanceValue = this.parseCurrency(
      this.mortgageBalanceTarget.value,
    );
    this.realtorComSellValue = this.parseCurrency(
      this.realtorComSellTarget.value,
    );
    this.purchasePriceValue = this.parseCurrency(
      this.purchasePriceTarget.value,
    );
    this.estimatedClosingCostValue = this.parseCurrency(
      this.estimatedClosingCostTarget.value,
    );
    this.realtorComBuyValue = this.parseCurrency(
      this.realtorComBuyTarget.value,
    );
    this.interestRateValue = this.parseCurrency(this.interestRateTarget.value);
    this.loanTermValue = this.parseCurrency(this.loanTermTarget.value);
  }

  updateEstCostSellValueFromInput() {
    this.estCostSellValue = this.parseCurrency(this.estCostSellTarget.value);
  }

  updateEstCostSellPercentageValueFromInput() {
    this.estCostSellPercentageValue = this.estCostSellPercentageTarget.value;
  }

  estProfitFromSellingValueChanged() {
    this.displayValuesInInputs();
    this.displayProfitSelling();
    this.updateFirstChartWithLabel();
  }

  homeValueChanged() {
    this.calculateEstCostSell();
    this.calculateEstCostSellPercentage();
    this.calculateProfitSelling();
  }

  calculateEstCostSell() {
    this.estCostSellValue =
      (this.estCostSellPercentageValue / 100) * this.homeValue;
  }

  calculateEstCostSellPercentage() {
    if (this.homeValue > 0) {
      this.estCostSellPercentageValue =
        (this.estCostSellValue / this.homeValue) * 100;
    }
  }

  calculateProfitSelling() {
    this.estProfitFromSellingValue =
      this.homeValue -
      this.mortgageBalanceValue -
      this.estCostSellValue -
      this.realtorComSellValue;
  }

  mortgageBalanceValueChanged() {
    this.calculateProfitSelling();
  }

  estCostSellValueChanged() {
    this.calculateEstCostSellPercentage();
    this.calculateProfitSelling();
  }

  estCostSellPercentageValueChanged() {
    this.calculateEstCostSell();
    this.calculateProfitSelling();
  }

  updateRealtorComSellValueFromInput() {
    this.realtorComSellValue = this.parseCurrency(
      this.realtorComSellTarget.value,
    );
  }

  updateRealtorComSellPercentageValueFromInput() {
    this.realtorComSellPercentageValue =
      this.realtorComSellPercentageTarget.value;
  }

  realtorComSellValueChanged() {
    this.calculateRealtorComSellPercentage();
    this.calculateProfitSelling();
  }

  realtorComSellPercentageValueChanged() {
    this.calculateRealtorComSell();
    this.calculateProfitSelling();
  }

  calculateRealtorComSellPercentage() {
    if (this.homeValue > 0) {
      this.realtorComSellPercentageValue =
        (this.realtorComSellValue / this.homeValue) * 100;
    }
  }

  calculateRealtorComSell() {
    this.realtorComSellValue =
      (this.realtorComSellPercentageValue / 100) * this.homeValue;
  }

  purchasePriceValueChanged() {
    this.calculateDownPaymentPercentage();
    this.calculateEstimatedClosingCostValue();
    this.calculateRealtorComBuy();
    this.calculateLoanAmount();
  }

  updateDownPaymentValueFromInput() {
    this.downPaymentValue = this.parseCurrency(this.downPaymentTarget.value);
  }

  updateDownPaymentPercentageValueFromInput() {
    this.downPaymentPercentageValue = this.downPaymentPercentageTarget.value;
  }

  downPaymentValueChanged() {
    this.calculateDownPaymentPercentage();
    this.calculateEstimatedCostsToBuy();
    this.calculateLoanAmount();
    this.updateFirstChartWithLabel();
  }

  downPaymentPercentageValueChanged() {
    this.calculateDownPaymentFromPercentage();
    this.calculateEstimatedCostsToBuy();
  }

  calculateDownPaymentPercentage() {
    if (this.purchasePriceValue > 0) {
      this.downPaymentPercentageValue =
        (this.downPaymentValue / this.purchasePriceValue) * 100;
    }
  }

  calculateDownPaymentFromPercentage() {
    this.downPaymentValue =
      (this.downPaymentPercentageValue / 100) * this.purchasePriceValue;
  }

  calculateEstimatedClosingCostValue() {
    this.estimatedClosingCostValue =
      (this.purchasePriceValue * this.estimatedClosingCostPercentageValue) /
      100;
  }

  updateEstimatedClosingCostPercentage() {
    this.estimatedClosingCostPercentageValue =
      this.estimatedClosingCostPercentageTarget.value;
  }

  estimatedClosingCostValueChanged() {
    this.calculateEstimatedClosingCostPercentage();
    this.calculateEstimatedCostsToBuy();
  }

  calculateEstimatedClosingCostPercentage() {
    if (this.purchasePriceValue > 0) {
      this.estimatedClosingCostPercentageValue =
        (this.estimatedClosingCostValue / this.purchasePriceValue) * 100;
    }
  }

  calculateEstimatedCostsToBuy() {
    this.estimatedCostsBuyValue =
      this.estimatedClosingCostValue + this.realtorComBuyValue;
  }

  estimatedClosingCostPercentageValueChanged() {
    this.calculateEstimatedClosingCostValue();
  }

  estimatedCostsBuyValueChanged() {
    this.displayValuesInInputs();
    this.updateFirstChartWithLabel();
  }

  calculateRealtorComBuy() {
    this.realtorComBuyValue =
      (this.purchasePriceValue * this.realtorComBuyPercentageValue) / 100;
  }

  updateRealtorComBuyPercentageFromInput() {
    this.realtorComBuyPercentageValue =
      this.realtorComBuyPercentageTarget.value;
  }

  realtorComBuyPercentageValueChanged() {
    this.calculateRealtorComBuy();
  }

  realtorComBuyValueChanged() {
    this.calculateRealtorComBuyPercentage();
    this.calculateEstimatedCostsToBuy();
  }

  calculateRealtorComBuyPercentage() {
    if (this.purchasePriceValue > 0) {
      this.realtorComBuyPercentageValue =
        (this.realtorComBuyValue / this.purchasePriceValue) * 100;
    }
  }

  calculateLoanAmount() {
    this.loanAmountValue = this.purchasePriceValue - this.downPaymentValue;
  }

  loanAmountValueChanged() {
    this.calculateEstimatedMortgagePayment();
    this.calculateOutstandingPrincipal();
    this.calculateEquityOnNewHomePayments();
    this.displayValuesInInputs();
  }

  calculateEstimatedMortgagePayment() {
    this.estimatedMortgagePaymentValue = this.calculateMonthlyPayment(
      this.loanAmountValue,
      this.interestRateValue,
      this.loanTermValue,
    );
  }

  interestRateValueChanged() {
    this.calculateEstimatedMortgagePayment();
    this.calculateOutstandingPrincipal();
  }

  loanTermValueChanged() {
    this.calculateEstimatedMortgagePayment();
    this.calculateOutstandingPrincipal();
  }

  estimatedMortgagePaymentValueChanged() {
    this.calculateMonthlyPaymentDifference();
    this.calculateOutstandingPrincipal();
    this.displayValuesInInputs();
  }

  updateAnnualAppreciationValueFromInput() {
    this.annualAppreciationValue = this.annualAppreciationTarget.value;
  }

  annualAppreciationValueChanged() {
    this.updateFirstChartWithLabel();
    this.updateSecondChartData();
  }

  calculateMonthlyPaymentDifference() {
    this.monthlyPaymentDifferenceValue =
      this.estimatedMortgagePaymentValue - this.currentMortgagePaymentValue;
  }

  monthlyPaymentDifferenceValueChanged() {
    this.calculateCumulativeMonthlyCostDifference();
  }

  calculateEquityOnNewHomePayments() {
    this.equityOnNewHomePaymentsValue =
      this.loanAmountValue - this.calculateOutstandingPrincipal();
  }

  calculateCumulativeMonthlyCostDifference() {
    this.cumulativeMonthlyCostDifferenceValue =
      this.monthlyPaymentDifferenceValue * 36;
  }

  cumulativeMonthlyCostDifferenceValueChanged() {
    this.displayValuesInInputs();
    this.updateFirstChartWithLabel();
  }

  calculateTotalCashDifference() {
    this.totalCashDifferenceValue =
      this.estProfitFromSellingValue -
      this.downPaymentValue -
      this.estimatedCostsBuyValue -
      this.cumulativeMonthlyCostDifferenceValue;
  }

  calculatetotalCashAndEquity() {
    this.totalCashAndEquityValue =
      this.equityAfterThreeYearsValue + this.totalCashDifferenceValue;
  }

  totalCashDifferenceValueChanged() {
    this.calculatetotalCashAndEquity();
    this.updateFirstChartWithLabel();
  }

  calculateOutstandingPrincipal() {
    const principal = this.loanAmountValue;
    const annualInterestRate = this.interestRateValue;
    const loanTermMonths = this.loanTermValue;

    if (
      !isNaN(principal) &&
      !isNaN(annualInterestRate) &&
      !isNaN(loanTermMonths)
    ) {
      const monthlyInterestRate = annualInterestRate / 12 / 100;
      const monthlyPayment = this.estimatedMortgagePaymentValue;
      // Calculate the outstanding balance after 36 payments
      const pow = Math.pow(1 + monthlyInterestRate, 36);
      const firstPart = pow * principal;
      const numerator = monthlyPayment * (pow - 1);
      const secondPart = numerator / monthlyInterestRate;
      const outstandingPrincipal = firstPart - secondPart;
      return outstandingPrincipal;
    } else {
      console.error("Invalid input for calculateOutstandingPrincipal");
      return 0;
    }
  }

  calculateMonthlyPayment(loanAmount, annualInterestRate, loanTerm_Months) {
    const monthly_rate = annualInterestRate / 12 / 100;
    const numerator = monthly_rate * (1 + monthly_rate) ** loanTerm_Months;
    const denominator = (1 + monthly_rate) ** loanTerm_Months - 1;
    return (loanAmount * numerator) / denominator;
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
  }

  parseCurrency(value) {
    return parseFloat(value.replace(/[$,]/g, "")) || 0;
  }

  updateFirstChartWithLabel() {
    if (!this.hasFirstChartWithLabelTarget) return;

    const estimatedProfit = this.estProfitFromSellingValue;
    const downPayment = this.downPaymentValue;
    const estimatedCostsBuy = this.estimatedCostsBuyValue;
    const cumulativeMonthlyCostDifference =
      this.cumulativeMonthlyCostDifferenceValue;
    const totalCashDifference =
      estimatedProfit -
      downPayment -
      estimatedCostsBuy -
      cumulativeMonthlyCostDifference;

    const pocketedSpent = totalCashDifference >= 0 ? "pocket" : "spend";
    this.pocketedSpentTarget.textContent = pocketedSpent;
    const doughnutLabelText = totalCashDifference >= 0 ? "Pocket" : "Spend";

    const purchasePrice = this.purchasePriceValue;
    const annualAppreciationPercentage = this.annualAppreciationValue;
    const appreciatedValue =
      purchasePrice * (1 + annualAppreciationPercentage / 100) ** 3 -
      purchasePrice;

    const equityInNewHome =
      appreciatedValue + downPayment + this.equityOnNewHomePaymentsValue;
    this.equityInNewHomeTarget.textContent =
      this.formatCurrency(equityInNewHome);

    this.superTotalTarget.textContent = this.formatCurrency(
      totalCashDifference + equityInNewHome,
    );
    this.newHomePriceTarget.textContent = this.formatCurrency(purchasePrice);
    this.estimatedProfitChartTarget.textContent =
      this.formatCurrency(estimatedProfit);
    this.estimatedCostToBuyChartTarget.textContent =
      this.formatCurrency(estimatedCostsBuy);
    this.monthlyCostChartTarget.textContent = this.formatCurrency(
      cumulativeMonthlyCostDifference,
    );
    this.downPaymentChartTarget.textContent = this.formatCurrency(downPayment);

    const colors = {
      estimatedProfitColor:
        this.estProfitFromSellingValue >= 0
          ? "rgba(46, 204, 113, 0.7)"
          : "rgba(231, 76, 60, 0.7)",
      estimatedProfitHoverColor:
        this.estProfitFromSellingValue >= 0
          ? "rgba(46, 204, 113, 1)"
          : "rgba(231, 76, 60, 1)",
      estimatedCostsBuyColor: "rgba(142, 68, 173, 0.7)",
      estimatedCostsBuyHoverColor: "rgba(142, 68, 173, 1)",
      downPaymentColor: "rgba(52, 152, 219, 0.7)",
      downPaymentHoverColor: "rgba(52, 152, 219, 1)",
      cumulativeMonthlyCostDifferenceColor:
        this.cumulativeMonthlyCostDifferenceValue >= 0
          ? "rgba(241, 196, 15, 0.7)"
          : "rgba(230, 126, 34, 0.7)",
      cumulativeMonthlyCostDifferenceHoverColor:
        this.cumulativeMonthlyCostDifferenceValue >= 0
          ? "rgba(241, 196, 15, 1)"
          : "rgba(230, 126, 34, 1)",
    };

    this.estimatedProfitColorTarget.style.backgroundColor =
      colors.estimatedProfitColor;
    this.estimatedCostToBuyColorTarget.style.backgroundColor =
      colors.estimatedCostsBuyColor;
    this.downPaymentColorTarget.style.backgroundColor = colors.downPaymentColor;
    this.monthlyCostColorTarget.style.backgroundColor =
      colors.cumulativeMonthlyCostDifferenceColor;

    const doughnutLabelPlugin = {
      id: "doughnutLabel",
      beforeDraw: function (chart) {
        if (
          chart.config.type !== "doughnut" ||
          !chart.config.options.plugins.doughnutLabel?.text
        )
          return;
        const { width, height, ctx } = chart;
        const { title, value } =
          chart.config.options.plugins.doughnutLabel.text;
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#000";
        ctx.font = "bold 20px Arial";
        ctx.fillText(title, width / 2, height / 2 - 10);
        ctx.font = "bold 24px Arial";
        ctx.fillText(value, width / 2, height / 2 + 20);
        ctx.restore();
      },
    };

    const chartData = {
      type: "doughnut",
      plugins: [doughnutLabelPlugin],
      data: {
        labels: [
          "Estimated Profit",
          "Down Payment",
          "Estimated Costs to Buy",
          "Cumulative Monthly Cost Difference",
        ],
        datasets: [
          {
            data: [
              Math.abs(estimatedProfit.toFixed()),
              downPayment.toFixed(),
              estimatedCostsBuy.toFixed(),
              Math.abs(cumulativeMonthlyCostDifference.toFixed()),
            ],
            backgroundColor: [
              colors.estimatedProfitColor,
              colors.estimatedCostsBuyColor,
              colors.downPaymentColor,
              colors.cumulativeMonthlyCostDifferenceColor,
            ],
            hoverBackgroundColor: [
              colors.estimatedProfitHoverColor,
              colors.estimatedCostsBuyHoverColor,
              colors.downPaymentHoverColor,
              colors.cumulativeMonthlyCostDifferenceHoverColor,
            ],
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          legend: { display: false },
          doughnutLabel: {
            text: {
              title: doughnutLabelText,
              value: this.formatCurrency(Math.abs(totalCashDifference)),
            },
          },
        },
      },
    };

    this.createOrUpdateChart(
      this.firstChartWithLabelTarget,
      chartData,
      "firstChartWithLabel",
    );
  }

  updateSecondChartData() {
    if (!this.hasSecondChartCanvasTarget) return;
    const annualAppreciationPercentage = this.annualAppreciationValue;
    const purchasePrice = this.purchasePriceValue;
    const appreciatedValue =
      purchasePrice * (1 + annualAppreciationPercentage / 100) ** 3 -
      purchasePrice;
    const downPayment = this.downPaymentValue;
    const equityOnNewHomePayments = this.equityOnNewHomePaymentsValue;

    this.appreciatedValueChartTarget.textContent =
      this.formatCurrency(appreciatedValue);
    this.downPayment2ChartTarget.textContent = this.formatCurrency(downPayment);
    this.equityInNewHomeChartTarget.textContent = this.formatCurrency(
      equityOnNewHomePayments,
    );

    const colors = {
      appreciatedValueColor:
        appreciatedValue >= 0
          ? "rgba(46, 204, 113, 0.7)"
          : "rgba(231, 76, 60, 0.7)",
      appreciatedValueHoverColor:
        appreciatedValue >= 0
          ? "rgba(46, 204, 113, 1)"
          : "rgba(231, 76, 60, 1)",
      downPaymentColor: "rgba(52, 152, 219, 0.7)",
      downPaymentHoverColor: "rgba(52, 152, 219, 1)",
      equityColor:
        equityOnNewHomePayments >= 0
          ? "rgba(155, 89, 182, 0.7)"
          : "rgba(243, 156, 18, 0.7)",
      equityHoverColor:
        equityOnNewHomePayments >= 0
          ? "rgba(155, 89, 182, 1)"
          : "rgba(243, 156, 18, 1)",
    };

    this.appreciatedValueColorTarget.style.backgroundColor =
      colors.appreciatedValueColor;
    this.downPaymentColor2Target.style.backgroundColor =
      colors.downPaymentColor;
    this.equityColorTarget.style.backgroundColor = colors.equityColor;

    const chartData = {
      type: "doughnut",
      data: {
        labels: [
          "Appreciated Value",
          "Down Payment",
          "Equity on New Home Payments",
        ],
        datasets: [
          {
            data: [
              Math.abs(appreciatedValue.toFixed()),
              downPayment.toFixed(),
              equityOnNewHomePayments.toFixed(),
            ],
            backgroundColor: [
              colors.appreciatedValueColor,
              colors.downPaymentColor,
              colors.equityColor,
            ],
            hoverBackgroundColor: [
              colors.appreciatedValueHoverColor,
              colors.downPaymentHoverColor,
              colors.equityHoverColor,
            ],
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        plugins: { legend: { display: false, position: "right" } },
      },
    };

    this.createOrUpdateChart(
      this.secondChartCanvasTarget,
      chartData,
      "secondChart",
    );
  }

  createOrUpdateChart(canvasTarget, chartData, chartInstanceName) {
    const ctx = canvasTarget.getContext("2d");
    if (this[chartInstanceName]) {
      this[chartInstanceName].data = chartData.data;
      this[chartInstanceName].options = chartData.options;
      this[chartInstanceName].update();
    } else {
      if (Chart.getChart(ctx)) {
        Chart.getChart(ctx).destroy();
      }
      this[chartInstanceName] = new Chart(ctx, chartData);
    }
  }

  disconnect() {
    if (this.firstChart) {
      this.firstChart.destroy();
      this.firstChart = null;
    }
    if (this.secondChart) {
      this.secondChart.destroy();
      this.secondChart = null;
    }
  }
}
